import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { clearReferencesField, findStatusTypeByPromotionID } from 'domains/promotion/Promotion.store';
import { CreatePromotionModel } from 'domains/promotion/Promotion.types';
import { Flex, InputNumber, MarginBox, Text } from 'UI';
import { hasData } from 'utils';
import { RemovePromotedProductsButton } from './RemovePromotedProductsButton';

export function ApplyDiscountReferences({
  promotion,
  setPromotion,
}: Readonly<{
  promotion: CreatePromotionModel;
  setPromotion: (x: CreatePromotionModel) => void;
}>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const referencesSections = useMemo(() => promotion.references ?? [{ items: [], discount: 0 }], [promotion]);

  const promotionStatus = useSelector((state: RootState) =>
    findStatusTypeByPromotionID(state, hasData(promotion) ? promotion.promotionId : undefined),
  );

  const handleRemoveReferences = (index?: number) => {
    if (index === undefined) return;
    const updatedReferences = [...(promotion.references ?? [])];
    if (index === 0 && updatedReferences.length === 1) {
      dispatch(clearReferencesField());
    } else {
      updatedReferences.splice(index, 1);
      setPromotion({ ...promotion, references: updatedReferences });
    }
  };

  return (
    <Flex direction={'column'} minWidth={210} maxWidth={210}>
      <Text type={'h5_bold'}>{t('backoffice.promotion.apply_discount', 'Apply discount')}</Text>
      {referencesSections.map((section, index) => {
        const isEditing = promotion.editing;
        const isSavedOrPlanned = promotionStatus === 'SAVED' || promotionStatus === 'PLANNED';
        const isPublished = promotionStatus === 'PUBLISHED';
        const isSectionEditing = promotion.editing && section.items.some((item) => !item.removable);
        const shouldRenderRemoveBtn = isEditing ? isSavedOrPlanned || (isPublished && !isSectionEditing) : true;

        return (
          <React.Fragment key={`reference-discount-${section.items.map((i) => i.item).join('-')}-${section.discount}`}>
            <MarginBox mb={20} />
            <Flex direction={'row'} align={'center'}>
              <Flex minWidth={115} maxWidth={115}>
                <InputNumber
                  disabled={isSectionEditing}
                  value={section.discount === 0 ? undefined : section.discount}
                  placeholder={'-'}
                  addonAfter={'%'}
                  min={0}
                  max={100}
                  data-cy={'references-discount-input'}
                  onChangeDebounced={(discount) => {
                    const updatedReferences = (promotion.references ?? []).map((s, i) =>
                      i === index ? { ...s, discount: Number(discount) || 0 } : s,
                    );
                    setPromotion({
                      ...promotion,
                      references: updatedReferences,
                    });
                  }}
                />
              </Flex>
              {shouldRenderRemoveBtn ? (
                <RemovePromotedProductsButton index={index} onDeleteClicked={handleRemoveReferences} />
              ) : (
                <MarginBox ml={60} />
              )}
            </Flex>
          </React.Fragment>
        );
      })}
    </Flex>
  );
}
